// ARCA-93 / WS2 — anonymous "Practice vs the bot" session bearer.
//
// The no-signup bot-practice identity. WS2 mints this device-local anon JWT
// (POST /api/auth/anon) when a guest enters practice and stores it HERE —
// deliberately NOT in $authBearer (stores/auth.ts):
//
//   - $authBearer presence drives AuthProvider → kind: 'authenticated', which
//     the WS1 route guard reads to admit a session onto auth-only routes
//     (/lobby, /leaderboard, …). An anon practice session must stay a GUEST so
//     ranked/social stays gated (ARCA-91 acceptance criterion 3). Keeping the
//     anon token out of $authBearer means the guard, AuthProvider, and silent
//     refresh need ZERO changes — a holder of an anon JWT is `kind: 'guest'`
//     by construction.
//   - The backend mints the anon JWT with a DISTINCT audience
//     (`runegambit-anon`) that `verifySession` REJECTS on ranked/social
//     endpoints (apps/backend/src/auth/anon-session.ts) — so this token is
//     usable ONLY for bot practice. This store is the client mirror of that.
//
// Persisted (device-local, like $authBearer) so a mid-practice reload can
// resume the same anon match (the match Durable Object is keyed by the anon
// uuid embedded in this JWT — re-minting would orphan the in-flight match).

import { persistentAtom } from '@nanostores/persistent'
import { $authBearer } from './auth.js'

export const ANON_STORAGE_KEY = 'runegambit.auth.anon'

/**
 * Anon practice bearer as persisted in localStorage. Carries ONLY the JWT and
 * its expiry — the anon uuid lives inside the JWT (`sub`) and the client never
 * needs to read it (B12 firewall: no game state here).
 */
export interface AnonBearerStored {
  jwt: string
  exp_ms: number
}

export const $anonBearer = persistentAtom<AnonBearerStored | null>(
  ANON_STORAGE_KEY,
  null,
  {
    encode: (value) => JSON.stringify(value),
    decode: (raw) => {
      if (!raw) return null
      try {
        return JSON.parse(raw) as AnonBearerStored
      } catch {
        // Malformed payload from a previous version — drop it; the next
        // practice entry mints a fresh anon session.
        return null
      }
    },
  },
)

/** Persist a freshly-minted anon practice session (auth/anon.ts mint path). */
export function saveAnonSession(jwt: string, exp_ms: number): void {
  $anonBearer.set({ jwt, exp_ms })
}

/** Clear the anon practice session. Called on logout-equivalent paths and on
 *  any 401 that rejected the anon token (net/client.ts, polling-fallback.ts). */
export function clearAnonSession(): void {
  $anonBearer.set(null)
}

/**
 * The JWT to present on a request, or null when there is no usable session.
 *
 * Precedence: a NON-EXPIRED real authenticated bearer always wins — a live
 * signed-in user keeps their identity on every request (and a still-valid
 * bearer behaves exactly as before this fix).
 *
 * ARCA-115: the real bearer is now expiry-filtered, symmetric with the anon
 * bearer below. The pre-fix code returned the real JWT unconditionally on the
 * theory that "apiFetch's 401→silent-refresh path owns real-bearer expiry" —
 * but that stranded a returning guest carrying a STALE/expired `$authBearer`
 * (e.g. left over from earlier sign-in testing): this helper reported a usable
 * session, so `ensureAnonSession()` (auth/anon.ts) no-op'd and never minted the
 * anon fallback, and `apiFetch` then sent the dead bearer → backend 401 on the
 * no-signup "Practice vs the bot" path. Treating an expired real bearer as "no
 * usable session" lets the anon fallback mint and the bot match start with zero
 * sign-in. The authenticated 401-recovery path is unaffected: forceSilentRefresh
 * / runRefresh read `$authBearer` directly, not this helper.
 *
 * Only when there is no usable real bearer do we fall back to a non-expired
 * anon practice token.
 */
export function effectiveBearerJwt(): string | null {
  const now = Date.now()
  const real = $authBearer.get()
  if (real && real.exp_ms > now) return real.jwt
  const anon = $anonBearer.get()
  if (anon && anon.exp_ms > now) return anon.jwt
  return null
}

/**
 * True when the effective session is an ANONYMOUS practice guest — i.e. there
 * is no usable real authenticated bearer, only a still-valid anon practice
 * token. This is exactly the case where `effectiveBearerJwt()` resolves to the
 * anon JWT (audience `runegambit-anon`).
 *
 * ARCA-493: authenticated-only endpoints (audience `runegambit-client`, e.g.
 * `GET /api/home` and `GET /api/profile/me`) reject the anon audience with 401,
 * and apiFetch turns that terminal 401 into a synchronous bounce to /splash
 * (`recoverToSplashOnAuthLoss`, the ARCA-94 safety net). Best-effort prefetches
 * use this predicate to SKIP those calls for a guest, rather than triggering a
 * spurious 401 that yanks the guest out of the bot match they just started.
 *
 * Returns `false` when there is NO usable session at all (neither bearer): a
 * guest whose anon token has actually expired is genuine auth loss, so the
 * ARCA-94 terminal-401 → /splash safety net must still fire for them (it is not
 * weakened here). Symmetric with the precedence in `effectiveBearerJwt`.
 */
export function isAnonPracticeSession(): boolean {
  const now = Date.now()
  const real = $authBearer.get()
  if (real && real.exp_ms > now) return false
  const anon = $anonBearer.get()
  return anon != null && anon.exp_ms > now
}
